import { Box, Typography, Button } from '@mui/material'
import { useNavigateBackDisabled } from 'pages/useNavigateBackDisabled'
import { Root, ContentContainer } from './styles'
import { useState } from 'react'
import { Header } from 'hello-design-system'
import { StudentThankYouScreenContent, FeedbackType, PLATFORM_WEB } from 'utils/constants'
import { InitialFeedbackScreen, DetailedFeedbackScreen, FeedbackSubmittedScreen } from './feedBack'
import { useStudentFeedback } from './feedBack/hooks/useStudentFeedback'
import { useSubmitStudentFeedback } from './feedBack/hooks/useSubmitStudentFeedback'
import type { FeedbackOption, SubmitFeedbackRequest } from './feedBack/feedbackTypes'

const StudentThankYouScreen: React.FC = () => {
    useNavigateBackDisabled()
    const coreCourseDashboardUrl = sessionStorage.getItem('callbackUrl') || process.env.REACT_APP_CORE_COURSE_WEB_URL!
    const { feedbackOptions, loading: isContentFetching, error } = useStudentFeedback()
    const { submitFeedback, loading: isSubmitting } = useSubmitStudentFeedback()
    const [selectedFeedback, setSelectedFeedback] = useState<typeof FeedbackType.POSITIVE | typeof FeedbackType.NEGATIVE | null>(null)
    const [selectedDetailedFeedback, setSelectedDetailedFeedback] = useState<string | null>(null)
    const [isSubmitted, setIsSubmitted] = useState(false)

    const goBackToDashboard = (): void => {
        window.location.href = coreCourseDashboardUrl
    }

    const handleFeedbackSelect = (feedback: typeof FeedbackType.POSITIVE | typeof FeedbackType.NEGATIVE): void => {
        setSelectedFeedback(feedback)
    }

    const handleDetailedFeedbackSelect = (reason: string): void => {
        setSelectedDetailedFeedback(reason)

        // Get the feedback type value (0 or 1)
        const feedbackTypeValue = selectedFeedback === FeedbackType.POSITIVE ? 1 : 0
        const studentFeedBack: SubmitFeedbackRequest = {
            feedbackType: feedbackTypeValue,
            reasons: [{ userReason: reason }],
            platform: PLATFORM_WEB
        }
        // Submit the feedback
        void submitFeedback(studentFeedBack)
            .then(() => {
                setIsSubmitted(true)
            })
            .catch(err => {
                setIsSubmitted(true)
                console.error('Failed to submit feedback:', err)
            })
    }

    const handleBack = (): void => {
        setSelectedFeedback(null)
        setSelectedDetailedFeedback(null)
    }

    const getSelectedFeedbackData = (): FeedbackOption | undefined => {
        if (!selectedFeedback || feedbackOptions.length === 0) return undefined

        const feedbackTypeValue = selectedFeedback === FeedbackType.POSITIVE ? 1 : 0
        return feedbackOptions.find(option => option.feedbackType === feedbackTypeValue)
    }

    const actionButton = (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Button
                id='book-your-next-class-button'
                color='primary'
                variant='contained'
                size='large'
                onClick={goBackToDashboard}>
                {StudentThankYouScreenContent.dashboardButton}
            </Button>
        </Box>
    )

    const renderFeedbackSection = (): React.ReactNode => {
        // Hide feedback if API failed
        if (error) {
            return actionButton
        }

        if (isSubmitted && !isSubmitting) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <FeedbackSubmittedScreen />
                    {actionButton}
                </Box>
            )
        }

        if (selectedFeedback) {
            const feedbackData = getSelectedFeedbackData()

            if (!feedbackData) {
                return (
                    <>
                        <InitialFeedbackScreen
                            selectedFeedback={selectedFeedback}
                            onFeedbackSelect={handleFeedbackSelect}
                            feedbackOptions={feedbackOptions}
                        />
                        {actionButton}
                    </>
                )
            }

            return (
                <DetailedFeedbackScreen
                    selectedFeedback={selectedFeedback}
                    selectedOption={selectedDetailedFeedback}
                    onOptionSelect={handleDetailedFeedbackSelect}
                    onBack={handleBack}
                    question={feedbackData.question}
                    options={feedbackData.options}
                />
            )
        }

        if (feedbackOptions.length) {
            return (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                    <InitialFeedbackScreen
                        selectedFeedback={selectedFeedback}
                        onFeedbackSelect={handleFeedbackSelect}
                        feedbackOptions={feedbackOptions}
                    />
                    {actionButton}
                </Box>
            )
        }

        return null
    }

    return (
        <Root>
            <Header />
            <ContentContainer>
                {!isSubmitted && <Typography variant='h3'>
                    {StudentThankYouScreenContent.title}
                </Typography>}
                {!isContentFetching && renderFeedbackSection()}
            </ContentContainer>
        </Root>
    )
}

export { StudentThankYouScreen }
